import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ReferenceDisplayDirective } from '../../shared/reference-display.directive';
import { HttpClient } from '@angular/common/http';
import { StateService } from '../../shared/state.service';
import { backendEndPointToken } from '../../app.config';
import { AuthService } from '../../shared/auth/auth.service';
import { FormFieldsSelectDataService } from '../../shared/form-fields-select-data.service';
import { isHospitalAdmin, normalizeRole } from '../../shared/system-roles';
import { forkJoin } from 'rxjs';

export type FilterMode = 'all' | 'day' | 'time' | 'week';

type TaskOwnerOption = {
    reference: string;
    display: string;
    type?: string;
};

@Component({
    selector: 'app-task-preview-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatMenuModule,
        MatTooltipModule,
        MatCheckboxModule,
        MatChipsModule,
        MatFormFieldModule,
        MatSelectModule,
        ReferenceDisplayDirective
    ],
    templateUrl: './task-preview-dialog.component.html',
    styleUrl: './task-preview-dialog.component.scss'
})
export class TaskPreviewDialogComponent implements OnInit {
    /** Converted from Map for template iteration */
    taskGroups: Array<{ groupId: string; tasks: any[] }> = [];

    /** Tracks which groups have the occurrence panel open */
    expandedGroups = new Set<string>();

    /** Filter mode per group: 'all' | 'day' | 'time' | 'week' */
    filterModes = new Map<string, FilterMode>();

    /** Selected filter value per group (date string, time string, or week label) */
    filterValues = new Map<string, string>();

    /** Selected task indices per group (for bulk actions) */
    selectedTasks = new Map<string, Set<number>>();

    totalTaskCount: number = 0;
    isExisting: boolean = false;

    private http = inject(HttpClient);
    private backend = inject(backendEndPointToken);
    private stateService = inject(StateService);
    private auth = inject(AuthService);
    private dialog = inject(MatDialog);
    private formFieldsDataService = inject(FormFieldsSelectDataService);

    patientName = '';
    patientPid = '';
    ownerOptions: TaskOwnerOption[] = [];
    loadingOwnerOptions = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            allTasks: any[];
            taskGroups: Map<string, any[]>;
            patientId: string;
            isExisting?: boolean;
        },
        private dialogRef: MatDialogRef<TaskPreviewDialogComponent>
    ) {
        this.isExisting = !!data.isExisting;
        this.taskGroups = Array.from(data.taskGroups.entries()).map(([groupId, tasks]) => ({
            groupId,
            tasks: tasks.map(task => this.cloneTask(task))
        }));
        this.totalTaskCount = data.allTasks.length;
        this.patientPid = data.patientId;
        this.patientName = `Patient/${data.patientId}`;
    }

    ngOnInit() {
        this.loadPatientDetails();
        this.loadOwnerOptions();
    }

    private loadPatientDetails() {
        // First try to get from StateService
        const currentPatient = this.stateService.PatientResources.currentPatient.value?.actualResource;
        if (currentPatient && currentPatient.id === this.data.patientId) {
            this.parsePatientResource(currentPatient);
            return;
        }

        // If not in StateService or mismatch, fetch from backend
        const patientId = this.data.patientId;
        if (!patientId || patientId === 'Unknown') return;

        this.http.get<any>(`${this.backend}/Patient/${patientId}`).subscribe({
            next: (patient) => {
                if (patient) {
                    this.parsePatientResource(patient);
                }
            },
            error: (err) => {
                console.error('Failed to load patient details for preview dialog', err);
            }
        });
    }

    private parsePatientResource(patient: any) {
        // Extract patient name
        const nameObj = patient.name?.[0] || {};
        if (nameObj.text) {
            this.patientName = nameObj.text;
        } else {
            const given = Array.isArray(nameObj.given) ? nameObj.given.join(' ') : (nameObj.given || '');
            const family = nameObj.family || '';
            this.patientName = `${given} ${family}`.trim() || patient.id;
        }

        // Extract pid identifier
        const pidIdentifier = patient.identifier?.find((ident: any) => 
            ident.system === 'https://elikita-server.daalitech.com/fhir/identifier/pid'
        );
        this.patientPid = pidIdentifier ? pidIdentifier.value : patient.id;
    }

    private loadOwnerOptions(): void {
        this.loadingOwnerOptions = true;

        forkJoin({
            practitioners: this.formFieldsDataService.getFormFieldSelectData('encounter', 'participant'),
            practitionerRoles: this.http.get<any>(`${this.backend}/PractitionerRole?_format=json&_count=100`)
        }).subscribe({
            next: ({ practitioners, practitionerRoles }) => {
                const practitionerOptions = Array.isArray(practitioners) ? practitioners : [];
                const practitionerRoleOptions = (practitionerRoles?.entry || [])
                    .map((entry: any) => {
                        const resource = entry.resource;
                        return {
                            reference: `PractitionerRole/${resource.id}`,
                            display: resource.code?.[0]?.coding?.[0]?.display || resource.code?.[0]?.text || `Role ${resource.id}`,
                            type: 'PractitionerRole'
                        };
                    })
                    .filter((option: TaskOwnerOption) => !!option.display && !option.display.includes('Role '));

                const uniqueRolesMap = new Map<string, TaskOwnerOption>();
                practitionerRoleOptions.forEach((role: TaskOwnerOption) => {
                    if (!uniqueRolesMap.has(role.display)) {
                        uniqueRolesMap.set(role.display, role);
                    }
                });

                this.ownerOptions = [...uniqueRolesMap.values(), ...practitionerOptions];
                this.loadingOwnerOptions = false;
            },
            error: (err) => {
                console.error('Failed to load task owner options', err);
                this.ownerOptions = [];
                this.loadingOwnerOptions = false;
            }
        });
    }

    // ── Getters ────────────────────────────────────────────────────────────────

    getGroupTitle(group: { groupId: string; tasks: any[] }): string {
        const firstTask = group.tasks[0];
        return firstTask?.description || firstTask?.code?.text || 'Task Group';
    }

    getResourceName(task: any): string {
        if (task.focus?.reference) {
            return task.focus.reference.split('/')[0].replace(/([A-Z])/g, ' $1').trim();
        }
        return task.code?.coding?.[0]?.display || 'General Task';
    }

    getTaskStatusSummary(tasks: any[]): string {
        const uniqueDates = new Set(tasks.map(t => {
            const iso = t.restriction?.period?.start || t.executionPeriod?.start;
            return iso ? new Date(iso).toLocaleDateString() : '';
        }).filter(Boolean));
        return `${tasks.length} occurrence${tasks.length !== 1 ? 's' : ''} across ${uniqueDates.size} day${uniqueDates.size !== 1 ? 's' : ''}`;
    }

    getRecurrencePattern(tasks: any[]): string {
        if (tasks.length === 1) return 'One Time';
        const dates = tasks
            .map(t => new Date(t.restriction?.period?.start || t.executionPeriod?.start))
            .filter(d => !isNaN(d.getTime()))
            .sort((a, b) => a.getTime() - b.getTime());
        if (dates.length < 2) return 'Custom';

        const diffs = dates.slice(1).map((d, i) =>
            Math.round((d.getTime() - dates[i].getTime()) / (1000 * 60 * 60 * 24))
        );
        if (diffs.every(d => d === 1)) return 'Daily';
        if (diffs.every(d => d === 7)) return 'Weekly';
        return 'Custom Schedule';
    }

    getEarliestDate(tasks: any[]): string {
        const dates = tasks.map(t => t.restriction?.period?.start || t.executionPeriod?.start).filter(Boolean).sort();
        return dates[0] ?? new Date().toISOString();
    }

    getLatestDate(tasks: any[]): string {
        const dates = tasks.map(t => t.restriction?.period?.start || t.executionPeriod?.start).filter(Boolean).sort();
        return dates[dates.length - 1] ?? new Date().toISOString();
    }

    getUniqueTimes(tasks: any[]): string[] {
        const set = new Set<string>();
        tasks.forEach(t => {
            const iso = t.restriction?.period?.start || t.executionPeriod?.start;
            if (iso) {
                const d = new Date(iso);
                set.add(`${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`);
            }
        });
        return Array.from(set).sort((a, b) => {
            const [ah, am] = a.split(':').map(Number);
            const [bh, bm] = b.split(':').map(Number);
            return (ah * 60 + am) - (bh * 60 + bm);
        });
    }

    getUniqueDays(tasks: any[]): string[] {
        const set = new Set<string>();
        tasks.forEach(t => {
            const iso = t.restriction?.period?.start || t.executionPeriod?.start;
            if (iso) set.add(new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
        });
        return Array.from(set).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    }

    getUniqueWeeks(tasks: any[]): string[] {
        const set = new Set<string>();
        tasks.forEach(t => {
            const iso = t.restriction?.period?.start || t.executionPeriod?.start;
            if (iso) {
                const d = new Date(iso);
                const weekStart = new Date(d);
                weekStart.setDate(d.getDate() - d.getDay());
                set.add(`Wk of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
            }
        });
        return Array.from(set);
    }

    // ── Filtered tasks for expansion panel ──────────────────────────────────

    getFilteredTasks(group: { groupId: string; tasks: any[] }): { task: any; originalIndex: number }[] {
        const mode = this.filterModes.get(group.groupId) ?? 'all';
        const value = this.filterValues.get(group.groupId) ?? '';

        return group.tasks.map((task, i) => ({ task, originalIndex: i })).filter(({ task }) => {
            if (mode === 'all' || !value) return true;
            const iso = task.restriction?.period?.start || task.executionPeriod?.start;
            if (!iso) return false;
            const d = new Date(iso);

            if (mode === 'day') {
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) === value;
            }
            if (mode === 'time') {
                const t = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                return t === value;
            }
            if (mode === 'week') {
                const ws = new Date(d);
                ws.setDate(d.getDate() - d.getDay());
                const label = `Wk of ${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                return label === value;
            }
            return true;
        });
    }

    // ── Expansion panel ──────────────────────────────────────────────────────

    toggleOccurrences(groupId: string): void {
        this.expandedGroups.has(groupId) ? this.expandedGroups.delete(groupId) : this.expandedGroups.add(groupId);
    }

    setFilter(groupId: string, mode: FilterMode, value = ''): void {
        this.filterModes.set(groupId, mode);
        this.filterValues.set(groupId, value);
        // Clear selections when filter changes
        this.selectedTasks.get(groupId)?.clear();
    }

    getFilterMode(groupId: string): FilterMode {
        return this.filterModes.get(groupId) ?? 'all';
    }

    getFilterValue(groupId: string): string {
        return this.filterValues.get(groupId) ?? '';
    }

    // ── Selection ────────────────────────────────────────────────────────────

    getSelectedSet(groupId: string): Set<number> {
        if (!this.selectedTasks.has(groupId)) this.selectedTasks.set(groupId, new Set());
        return this.selectedTasks.get(groupId)!;
    }

    isSelected(groupId: string, originalIndex: number): boolean {
        return this.getSelectedSet(groupId).has(originalIndex);
    }

    toggleTaskSelection(groupId: string, originalIndex: number): void {
        const s = this.getSelectedSet(groupId);
        s.has(originalIndex) ? s.delete(originalIndex) : s.add(originalIndex);
    }

    selectAllVisible(group: { groupId: string; tasks: any[] }): void {
        const s = this.getSelectedSet(group.groupId);
        this.getFilteredTasks(group).forEach(({ originalIndex }) => s.add(originalIndex));
    }

    deselectAll(groupId: string): void {
        this.getSelectedSet(groupId).clear();
    }

    getSelectedCount(groupId: string): number {
        return this.getSelectedSet(groupId).size;
    }

    isAllVisibleSelected(group: { groupId: string; tasks: any[] }): boolean {
        const visible = this.getFilteredTasks(group);
        if (!visible.length) return false;
        return visible.every(({ originalIndex }) => this.isSelected(group.groupId, originalIndex));
    }

    // ── Group actions ────────────────────────────────────────────────────────

    /**
     * Remove selected tasks from this group's submission list.
     * In FHIR R4, Task.status = 'cancelled' is valid — this removes them before creation.
     */
    removeSelectedTasks(group: { groupId: string; tasks: any[] }): void {
        const selected = this.getSelectedSet(group.groupId);
        if (!selected.size) return;
        // Remove in reverse index order to preserve indices
        const indicesToRemove = Array.from(selected).sort((a, b) => b - a);
        indicesToRemove.forEach(i => group.tasks.splice(i, 1));
        selected.clear();
        // Recompute total
        this.totalTaskCount = this.taskGroups.reduce((sum, g) => sum + g.tasks.length, 0);
    }

    /**
     * Mark selected tasks as 'cancelled' status (they will be submitted as cancelled).
     * FHIR R4 allows creating Tasks with status = 'cancelled' if needed.
     */
    markSelectedCancelled(group: { groupId: string; tasks: any[] }): void {
        const selected = this.getSelectedSet(group.groupId);
        selected.forEach(i => {
            if (group.tasks[i]) group.tasks[i].status = 'cancelled';
        });
        selected.clear();
    }

    reassignSelected(group: { groupId: string; tasks: any[] }): void {
        const selected = this.getSelectedSet(group.groupId);
        if (!selected.size) return;

        this.openOwnerPicker().afterClosed().subscribe((owner: TaskOwnerOption | undefined) => {
            if (!owner?.reference) return;

            selected.forEach(i => {
                this.setTaskOwner(group.tasks[i], owner);
            });
            selected.clear();
        });
    }

    reassignTaskAt(group: { groupId: string; tasks: any[] }, originalIndex: number): void {
        const task = group.tasks[originalIndex];
        if (!task) return;

        this.openOwnerPicker(task.owner).afterClosed().subscribe((owner: TaskOwnerOption | undefined) => {
            if (!owner?.reference) return;
            this.setTaskOwner(task, owner);
        });
    }

    private openOwnerPicker(currentOwner?: any) {
        return this.dialog.open(TaskOwnerPickerDialogComponent, {
            width: '520px',
            maxWidth: '95vw',
            data: {
                ownerOptions: this.ownerOptions,
                loadingOwnerOptions: this.loadingOwnerOptions,
                currentOwner
            }
        });
    }

    private setTaskOwner(task: any, owner: TaskOwnerOption): void {
        if (!task) return;
        task.owner = {
            reference: owner.reference,
            display: owner.display || undefined
        };
    }

    // ── Formatting ───────────────────────────────────────────────────────────

    formatTime(isoString: string): string {
        if (!isoString) return '—';
        return new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    }

    formatDate(isoString: string): string {
        if (!isoString) return '—';
        return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    formatTimeString(timeStr: string): string {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${period}`;
    }

    getDurationMins(startIso: string, endIso: string): number {
        return Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
    }

    // ── Submission ───────────────────────────────────────────────────────────

    confirm(): void {
        const allTasks = this.taskGroups.flatMap(g => g.tasks).map(task => this.sanitizeTaskForPreviewResult(task));
        this.dialogRef.close({ confirmed: true, tasks: allTasks });
    }

    cancel(): void {
        this.dialogRef.close({ confirmed: false });
    }

    private cloneTask<T>(task: T): T {
        return task ? JSON.parse(JSON.stringify(task)) : task;
    }

    private sanitizeTaskForPreviewResult(task: any): any {
        const cleanTask = this.cloneTask(task);
        [
            'patientDetails',
            'startDate',
            'dueDate',
            'taskName',
            'assignedTo',
            'overdue',
            'focusCategory'
        ].forEach(key => delete cleanTask[key]);

        Object.keys(cleanTask).forEach(key => {
            if (key.startsWith('_')) {
                delete cleanTask[key];
            }
        });

        return cleanTask;
    }

    // ── Reassignment Capability Checks ───────────────────────────────────────

    getTaskFocusCategory(task: any): 'laboratory' | 'imaging' | 'observation' | 'medication' | 'unknown' {
        const focusRef = task.focus?.reference || '';
        if (focusRef.startsWith('MedicationRequest/')) {
            return 'medication';
        }

        if (!focusRef.startsWith('ServiceRequest/')) {
            return 'unknown';
        }

        const srId = focusRef.split('/')[1] || focusRef;

        // Try to find the ServiceRequest resource in the dialog data bundle
        let serviceRequestResource: any = null;
        if ((this.data as any).bundle?.entry) {
            const entry = (this.data as any).bundle.entry.find((e: any) => 
                e.resource?.resourceType === 'ServiceRequest' && 
                (e.resource.id === srId || `urn:uuid:${e.resource.id}` === focusRef || e.fullUrl === focusRef)
            );
            if (entry) {
                serviceRequestResource = entry.resource;
            }
        }

        // Try to find in StateService PatientResources
        if (!serviceRequestResource) {
            const cached = this.stateService.PatientResources.serviceRequests.value?.find((r: any) => 
                r.actualResource?.id === srId || r.referenceId === focusRef
            );
            if (cached) {
                serviceRequestResource = cached.actualResource;
            }
        }

        // Try to find in StateService orgWideResources
        if (!serviceRequestResource) {
            const cached = this.stateService.orgWideResources.serviceRequests.value?.find((r: any) => 
                r.actualResource?.id === srId || r.referenceId === focusRef
            );
            if (cached) {
                serviceRequestResource = cached.actualResource;
            }
        }

        if (serviceRequestResource) {
            const categories = serviceRequestResource.category || [];
            const isLab = categories.some((c: any) => 
                (c.text && c.text.toLowerCase().includes('lab')) || 
                (c.coding && c.coding.some((co: any) => co.code === 'laboratory' || (co.display && co.display.toLowerCase().includes('lab'))))
            );
            if (isLab) return 'laboratory';

            const isImaging = categories.some((c: any) => 
                (c.text && c.text.toLowerCase().includes('imaging')) || 
                (c.coding && c.coding.some((co: any) => co.code === 'imaging' || (co.display && co.display.toLowerCase().includes('imaging'))))
            );
            if (isImaging) return 'imaging';

            const isObs = categories.some((c: any) => 
                (c.text && c.text.toLowerCase().includes('observation')) || 
                (c.coding && c.coding.some((co: any) => co.code === 'observation' || (co.display && co.display.toLowerCase().includes('observation'))))
            );
            if (isObs) return 'observation';
        }

        const textToCheck = `${task.description || ''} ${task.code?.text || ''} ${task.code?.coding?.[0]?.display || ''}`.toLowerCase();
        
        if (textToCheck.includes('medication') || textToCheck.includes('drug') || textToCheck.includes('prescrip')) {
            return 'medication';
        }
        if (textToCheck.includes('vital') || textToCheck.includes('bp') || textToCheck.includes('blood pressure') || textToCheck.includes('temp') || textToCheck.includes('pulse') || textToCheck.includes('observation')) {
            return 'observation';
        }
        if (textToCheck.includes('lab') || textToCheck.includes('blood test') || textToCheck.includes('urine') || textToCheck.includes('sputum') || textToCheck.includes('test')) {
            return 'laboratory';
        }
        if (textToCheck.includes('xray') || textToCheck.includes('x-ray') || textToCheck.includes('scan') || textToCheck.includes('mri') || textToCheck.includes('ultrasound') || textToCheck.includes('imaging')) {
            return 'imaging';
        }

        return 'unknown';
    }

    canReassignTask(task: any): boolean {
        const category = this.getTaskFocusCategory(task);
        if (category === 'medication') {
            return false;
        }

        const currentUser = this.auth.getCurrentUser();
        if (!currentUser) return false;

        const role = (currentUser.role || '').toLowerCase();
        const roles = (currentUser.roles || []).map((r: string) => r.toLowerCase());

        const isLabTech = role === 'labtechnician' || roles.includes('labtechnician');
        const isAdmin = isHospitalAdmin(role) || roles.some(r => isHospitalAdmin(r)) || !!currentUser.isSuperAdmin;
        const isNurse = role === 'healthcareassistant' || roles.includes('healthcareassistant');

        if (category === 'laboratory' || category === 'imaging') {
            return isLabTech || isAdmin;
        }

        if (category === 'observation') {
            return isNurse || isAdmin;
        }

        // Restrict other/unknown tasks reassignment to admin and labtechnician
        return isAdmin || isLabTech;
    }

    canReassignGroup(group: { groupId: string; tasks: any[] }): boolean {
        if (!group.tasks || group.tasks.length === 0) return false;
        return this.canReassignTask(group.tasks[0]);
    }

    canCancelTask(task: any): boolean {
        const currentUser = this.auth.getCurrentUser();
        if (!currentUser) return false;
        if (currentUser.isSuperAdmin || isHospitalAdmin(currentUser.role)) {
            return true;
        }
        if (!task.requester?.reference) {
            return true; // For backwards compatibility
        }
        const userRef = currentUser.fhirResourceId ? `Practitioner/${currentUser.fhirResourceId}` : null;
        return !!userRef && task.requester.reference === userRef;
    }

    canCancelGroup(group: { groupId: string; tasks: any[] }): boolean {
        if (!group.tasks || group.tasks.length === 0) return false;
        return group.tasks.every(t => this.canCancelTask(t));
    }

    canCancelSelected(group: { groupId: string; tasks: any[] }): boolean {
        const selected = this.getSelectedSet(group.groupId);
        if (selected.size === 0) return false;
        return Array.from(selected).every(idx => {
            const task = group.tasks[idx];
            return task ? this.canCancelTask(task) : false;
        });
    }
}

@Component({
    selector: 'app-task-owner-picker-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatSelectModule
    ],
    template: `
        <div class="owner-picker-dialog">
            <h2 mat-dialog-title>
                <mat-icon>assignment_ind</mat-icon>
                Reassign Task
            </h2>

            <mat-dialog-content>
                <mat-form-field appearance="outline" class="owner-field">
                    <mat-label>Assign To (Role or Practitioner)</mat-label>
                    <mat-select [(ngModel)]="selectedOwner" placeholder="Select Assignee" [disabled]="data.loadingOwnerOptions">
                        @for (opt of data.ownerOptions; track opt.reference) {
                        <mat-option [value]="opt">
                            <span>{{ opt.display }}</span>
                            <span class="owner-ref">{{ opt.reference }}</span>
                        </mat-option>
                        }
                    </mat-select>
                    @if (data.loadingOwnerOptions) {
                    <mat-hint>Loading assignees...</mat-hint>
                    } @else if (!data.ownerOptions?.length) {
                    <mat-hint>No roles or practitioners found.</mat-hint>
                    }
                </mat-form-field>

                @if (data.currentOwner?.reference) {
                <div class="current-owner">
                    <span>Current owner</span>
                    <strong>{{ data.currentOwner.display || data.currentOwner.reference }}</strong>
                </div>
                }
            </mat-dialog-content>

            <mat-dialog-actions align="end">
                <button mat-button mat-dialog-close>Cancel</button>
                <button mat-raised-button color="primary" [disabled]="!selectedOwner" (click)="confirm()">
                    Reassign
                </button>
            </mat-dialog-actions>
        </div>
    `,
    styles: [`
        .owner-picker-dialog h2 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
        }

        .owner-field {
            width: 100%;
            margin-top: 8px;
        }

        .owner-ref {
            display: block;
            font-size: 11px;
            color: var(--mat-sys-on-surface-variant);
        }

        .current-owner {
            margin-top: 12px;
            padding: 10px;
            border: 1px solid var(--mat-sys-outline-variant);
            border-radius: 6px;
            display: flex;
            flex-direction: column;
            gap: 3px;
        }

        .current-owner span {
            font-size: 12px;
            color: var(--mat-sys-on-surface-variant);
        }
    `]
})
export class TaskOwnerPickerDialogComponent {
    selectedOwner?: TaskOwnerOption;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            ownerOptions: TaskOwnerOption[];
            loadingOwnerOptions: boolean;
            currentOwner?: any;
        },
        private dialogRef: MatDialogRef<TaskOwnerPickerDialogComponent>
    ) {
        const currentReference = data.currentOwner?.reference;
        this.selectedOwner = data.ownerOptions?.find(option => option.reference === currentReference);
    }

    confirm(): void {
        this.dialogRef.close(this.selectedOwner);
    }
}
